import { datadogRum } from '@datadog/browser-rum';
import { useRouter } from '@tanstack/react-router';
import { HTTPError } from 'ky';
import { type ErrorInfo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { TempoApiError } from '@/common/api/tempoApiTypes';
import { DefaultError } from '@/common/components/error/DefaultError';
import { isPermissionDeniedError } from '@/common/hooks/usePermissions';
import { goToLogout } from '@/common/utils/authRedirect';
import { NotFoundError } from './NotFoundError';

interface GlobalErrorProps {
  error?: Error;
  errorInfo?: ErrorInfo;
}

export const GlobalError = ({ error, errorInfo }: GlobalErrorProps) => {
  const { t } = useTranslation();
  const router = useRouter();
  let statusCode: number | undefined;

  if (error instanceof TempoApiError) {
    statusCode = error.error.status;
  } else if (error instanceof HTTPError) {
    statusCode = error.response.status;
  }

  const isPermissionError = error !== undefined && isPermissionDeniedError(error);

  useEffect(() => {
    if (error && !isPermissionError) {
      datadogRum.addError(error, {
        source: 'react-error-boundary',
        type: 'global-error',
        stack: error.stack,
        message: error.message,
        componentStack: errorInfo?.componentStack,
      });
    }
  }, [error, errorInfo, isPermissionError]);

  const goBackAction = {
    label: t(($) => $.common.goBack),
    action: () => {
      if (router.history.canGoBack()) {
        router.history.back();
      } else {
        router.navigate({ to: '/' });
      }
    },
  };

  if (isPermissionError) {
    return (
      <DefaultError
        title={t(($) => $.common.errors.permission.title)}
        description={t(($) => $.common.errors.permission.description)}
        actions={[goBackAction]}
      />
    );
  }

  if (statusCode === 404) {
    return <NotFoundError />;
  }

  return (
    <DefaultError
      title={t(($) => $.common.errors.global.title)}
      description={t(($) => $.common.errors.global.description)}
      actions={[
        { ...goBackAction, variant: 'primary' },
        {
          label: t(($) => $.common.logout),
          action: () => goToLogout(),
          variant: 'secondary',
        },
      ]}
    />
  );
};
